import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  Injector,
  input,
  OnInit,
  output,
  Signal,
  signal,
  WritableSignal,
  ChangeDetectionStrategy
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { FormsHelpers } from '@iot-platform/iot-platform-utils';
import { Filter, LabelValue } from '@iot-platform/models/common';
import { get } from 'lodash';
import { map } from 'rxjs';
import { FormControlType, FormField } from '../../../forms';
import { requiresValue as requiresValueHelper } from '../../helpers';
import { FilterFieldOptions, FilterFieldV2 } from '../../models';
import { FilterEngineV2Store } from '../../state/filter-engine-v2.store';

export enum FilterEngineFormControlNames {
  OPERATOR = 'operator',
  SEARCH_TERM = 'searchTerm'
}

export interface FilterEngineFormValue {
  [FilterEngineFormControlNames.OPERATOR]: LabelValue;
  [FilterEngineFormControlNames.SEARCH_TERM]: string;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  template: ''
})
export abstract class AbstractFilterEngineFormComponent<T> implements OnInit, AfterViewInit {
  readonly store = inject(FilterEngineV2Store);
  readonly field = input.required<FilterFieldV2>();
  readonly filterApplied = output<{
    filter: Filter;
    field: FilterFieldV2;
  }>();
  readonly canApply = output<boolean>();
  readonly isAutoApply = this.store.autoApply;
  readonly multipleOperatorSupport = this.store.multipleOperatorSupport;
  readonly disabled = this.store.disabled;
  elementRef!: ElementRef;
  onReset = new EventEmitter<void>();
  onApply = new EventEmitter<void>();
  fieldOptions: Signal<Partial<FilterFieldOptions>> = computed(() => {
    const field = this.field();
    return field?.options;
  });
  readonly availableOperators = computed(() => {
    const field = this.field();
    const operators = get(field, 'operators', []);
    return operators.map((value) => ({ label: value, value }));
  });
  formValueChanges: Signal<FilterEngineFormValue>;
  fields: WritableSignal<Partial<FormField>[]> = signal([]);
  selectedOperator: WritableSignal<LabelValue | null> = signal(null);
  form: FormGroup<any> = new FormGroup({
    [FilterEngineFormControlNames.OPERATOR]: new FormControl<LabelValue | null>(null),
    [FilterEngineFormControlNames.SEARCH_TERM]: new FormControl<T | null>(null)
  });
  requiresValue = computed(() => {
    const formValueChanges = this.formValueChanges();
    const operator = get(formValueChanges, FilterEngineFormControlNames.OPERATOR);
    return requiresValueHelper(operator?.value);
  });
  public operatorField = {
    type: FormControlType.DROP_DOWN_LIST,
    name: signal(FilterEngineFormControlNames.OPERATOR),
    items: this.availableOperators,
    fxFlex: signal('100%'),
    required: signal(true),
    trackBy: (item: LabelValue) => item?.value,
    displayBy: (item: LabelValue) => `AG_GRID.${item?.label}`,
    selectionChange: (event: { value: LabelValue }) => this.onOperatorSelectionChange(event.value),
    initialValue: computed(() => this.getDefaultOperator()),
    disabled: this.disabled
  };
  public searchTermField = {
    type: FormControlType.TEXT,
    name: signal(FilterEngineFormControlNames.SEARCH_TERM),
    label: computed(() => {
      const options = this.fieldOptions();
      return get(options, 'criteriaLabel', 'AG_GRID.searchOoo');
    }),
    fxFlex: signal('100%'),
    onReady: (params: any) => {
      this.elementRef = params.ref;
    },
    onKeyupEnter: () => this.onKeyupEnter(),
    disabled: this.disabled,
    visible: this.requiresValue
  };
  readonly currentFilters = computed(() => {
    const field = this.field();
    const getFiltersForField = this.store.getFiltersForField();
    return getFiltersForField(field?.options?.criteriaKey as string);
  });
  protected readonly destroyRef = inject(DestroyRef);
  protected readonly injector: Injector = inject(Injector);
  protected readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

  constructor() {
    this.formValueChanges = toSignal(
      this.form.valueChanges.pipe(
        map(() => this.form?.getRawValue()),
        takeUntilDestroyed(this.destroyRef)
      )
    ) as Signal<FilterEngineFormValue>;

    this.onReset.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.handleReset());
    this.onApply.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.handleApply());

    effect(() => {
      const requiresValue = this.requiresValue();
      if (!requiresValue) {
        this.clearValue();
      }
    });
    effect(() => {
      this.formValueChanges();
      const disabled = this.disabled();
      const requiresValue = this.requiresValue();
      let canApply = !requiresValue;
      if (!requiresValue) {
        canApply = true;
      } else {
        canApply = this.form.valid && !disabled && this.hasValidValue();
      }
      this.canApply.emit(canApply);
    });
  }

  get operatorControl() {
    return this.form.get(FilterEngineFormControlNames.OPERATOR);
  }

  get searchTermControl() {
    return this.form.get(FilterEngineFormControlNames.SEARCH_TERM);
  }

  onOperatorSelectionChange(operator: LabelValue) {
    this.selectedOperator.set(operator);
  }

  abstract getValueDisplay(data?: any): any;

  abstract getControlValue(): any;

  abstract hasValidValue(): boolean;

  abstract getSearchFields(): Partial<FormField>[];

  ngOnInit() {
    this.initializeFields();
  }

  ngAfterViewInit() {
    const timeout = setTimeout(() => {
      this.elementRef?.nativeElement?.focus();
      clearTimeout(timeout);
    }, 0);
  }

  initializeFields(): void {
    const suppressOperator = this.store.suppressOperator();
    const fields = [];
    if (!suppressOperator) {
      fields.push(this.operatorField);
    }
    fields.push(...this.getSearchFields());
    this.fields.set(fields);
    this.onOperatorSelectionChange(this.getDefaultOperator());
  }

  onKeyupEnter() {
    if (this.isAutoApply()) {
      this.handleApply();
    }
  }

  // Get the default operator considering current filters and field default
  getDefaultOperator(): LabelValue {
    const field = this.field();
    const operators = this.availableOperators();
    const suppressOperator = this.store.suppressOperator();
    const currentFilters = this.currentFilters();
    if (!suppressOperator && currentFilters?.length) {
      const last = currentFilters[currentFilters.length - 1];
      const foundLast = operators.find((o) => o.value === last?.criteriaOperator);
      if (foundLast) {
        return foundLast;
      }
    }
    const found = operators.find((op) => op.value === field?.defaultOperator);
    return found ?? operators[0];
  }

  handleReset() {
    const currentFilters = this.currentFilters();
    if (currentFilters?.length) {
      const defaultOp = this.getDefaultOperator();
      this.operatorControl?.reset(defaultOp);
      this.selectedOperator.set(defaultOp);
      this.clearValue();

      const field = this.field();
      const filters = this.store.appliedFilters();
      const filteredFilters = filters.filter((f) => f.criteriaKey !== field?.key);
      this.store.setAppliedFilters([...filteredFilters]);
      this.emitFilter(null);
    }
  }

  handleApply(): void {
    if (FormsHelpers.isFormValid(this.form)) {
      const operator = this.selectedOperator();
      if (!this.requiresValue()) {
        this.emitFilter(null, operator?.value);
      } else {
        const value = this.getControlValue();
        const valueDisplay = this.getValueDisplay();
        this.emitFilter(value, valueDisplay);
        this.clearValue();
      }
    }
  }

  clearValue(): void {
    this.searchTermControl?.reset();
  }

  emitFilter(value: any, valueDisplay?: string): void {
    const field = this.field();
    const operator = this.selectedOperator();
    if (!operator) {
      console.warn('[FilterField] No operator selected for field:', field.key);
      return;
    }
    const filter: Filter = {
      criteriaKey: field.options.criteriaKey,
      criteriaLabel: field.fullLabel,
      criteriaOperator: operator.value,
      value,
      valueDisplay,
      label: valueDisplay // for filter engine v1 compatibility
    };

    this.filterApplied.emit({ filter, field });
  }
}
